import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchWithAuth } from "../utils/api";
import "./BottomNav.css";

// Icônes en trait fin (style Feather), sans emoji.
const IconMap = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
    <line x1="8" y1="2" x2="8" y2="18" />
    <line x1="16" y1="6" x2="16" y2="22" />
  </svg>
);
const IconUsers = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconUser = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconCalendar = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ITEMS = [
  { key: "home", path: "/", label: "nav.map", icon: IconMap },
  { key: "community", path: "/community", label: "nav.community", icon: IconUsers },
  { key: "dives", path: "/dives", label: "nav.dives", icon: IconCalendar },
  { key: "profile", path: "/profile", label: "nav.profile", icon: IconUser },
];

const isActive = (path, pathname) =>
  path === "/" ? pathname === "/" : pathname.startsWith(path);

export default function BottomNav({ currentUser }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [inviteCount, setInviteCount] = useState(0);
  const [friendCount, setFriendCount] = useState(0);
  const apiUrl = process.env.REACT_APP_API_URL;
  const userId = currentUser?.id;

  // Pastilles d'actions en attente, à jour dans toute l'app : invitations (sur
  // "Plongées") et demandes d'amis reçues (sur "Profil"). Rechargées à chaque
  // changement de page + via les events émis quand on accepte/refuse.
  useEffect(() => {
    let active = true;
    const loadInvites = async () => {
      try {
        const res = await fetchWithAuth(`${apiUrl}/my-invitations`);
        const data = await res.json();
        if (active) setInviteCount(Array.isArray(data) ? data.length : 0);
      } catch { if (active) setInviteCount(0); }
    };
    const loadFriends = async () => {
      if (!userId) { setFriendCount(0); return; }
      try {
        const res = await fetchWithAuth(`${apiUrl}/friend-requests/received/${userId}`);
        const data = await res.json();
        if (active) setFriendCount(Array.isArray(data) ? data.length : 0);
      } catch { if (active) setFriendCount(0); }
    };
    loadInvites();
    loadFriends();
    window.addEventListener("invitations-changed", loadInvites);
    window.addEventListener("friend-requests-changed", loadFriends);
    return () => {
      active = false;
      window.removeEventListener("invitations-changed", loadInvites);
      window.removeEventListener("friend-requests-changed", loadFriends);
    };
  }, [pathname, apiUrl, userId]);

  const badgeFor = (key) => (key === "dives" ? inviteCount : key === "profile" ? friendCount : 0);

  const renderItem = (it) => {
    const active = isActive(it.path, pathname);
    const count = badgeFor(it.key);
    return (
      <button
        key={it.key}
        type="button"
        className={active ? "active" : ""}
        aria-current={active ? "page" : undefined}
        onClick={() => navigate(it.path)}
        style={{ position: "relative" }}
      >
        {it.icon}
        {count > 0 && (
          <span style={{ position: "absolute", top: 2, left: "50%", transform: "translateX(3px)", minWidth: 15, height: 15, padding: "0 3px", boxSizing: "border-box", borderRadius: 999, background: "#e0392b", color: "#fff", fontSize: 9.5, fontWeight: 700, lineHeight: "15px", textAlign: "center" }}>{count}</span>
        )}
        <span>{t(it.label)}</span>
      </button>
    );
  };

  return (
    <nav className="bottom-nav">
      {ITEMS.slice(0, 2).map(renderItem)}

      {/* Bouton de création rapide (au centre, mis en avant) */}
      <button
        type="button"
        className="create-btn"
        onClick={() => navigate("/?create=1")}
        aria-label={t("create.button")}
      >
        <span className="create-circle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
        <span>{t("create.button")}</span>
      </button>

      {ITEMS.slice(2).map(renderItem)}
    </nav>
  );
}
